<!--
  Screenshot Panel — right-drawer gallery
  ========================================
  Mounted at the takeoffs-detail host root. [open]=true triggers the slide-in.
-->
<ng-container *ngIf="open">
  <!-- Scrim backdrop -->
  <div class="ss-panel-backdrop" @fadeBackdrop (click)="close()" aria-hidden="true"></div>

  <!-- Right drawer -->
  <aside
    class="ss-panel"
    role="dialog"
    aria-label="Screenshots gallery"
    @slideInRight
  >
    <!-- Header -->
    <header class="ss-panel-header">
      <div class="ss-panel-title-block">
        <h2 class="ss-panel-title" *ngIf="!selectMode">Screenshots</h2>
        <h2 class="ss-panel-title" *ngIf="selectMode">{{ selectedCount }} selected</h2>
        <p class="ss-panel-subtitle">
          <ng-container *ngIf="!selectMode">{{ totalCount }} clip{{ totalCount === 1 ? '' : 's' }} captured</ng-container>
          <ng-container *ngIf="selectMode">of {{ visibleCount }} visible</ng-container>
        </p>
      </div>

      <div class="ss-panel-header-actions">
        <button
          *ngIf="!selectMode"
          type="button"
          class="ss-panel-btn ss-panel-btn-ghost"
          (click)="toggleSelectMode()"
        >
          Select
        </button>
        <button
          *ngIf="selectMode"
          type="button"
          class="ss-panel-btn ss-panel-btn-ghost"
          (click)="cancelSelectMode()"
        >
          Cancel
        </button>
        <button
          *ngIf="!selectMode"
          type="button"
          class="ss-panel-btn ss-panel-btn-primary"
          (click)="newCapture.emit()"
        >
          <mat-icon>add</mat-icon>
          <span>New</span>
        </button>
        <button
          type="button"
          class="ss-panel-btn ss-panel-btn-icon"
          matTooltip="Close (Esc)"
          aria-label="Close panel"
          (click)="close()"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </header>

    <!-- Search row -->
    <div class="ss-panel-search">
      <mat-icon class="ss-panel-search-icon" aria-hidden="true">search</mat-icon>
      <input
        type="text"
        class="ss-panel-search-input"
        placeholder="Search captions, pages, measurements…"
        [formControl]="searchControl"
        aria-label="Search screenshots"
      />
      <button
        *ngIf="searchControl.value"
        type="button"
        class="ss-panel-search-clear"
        matTooltip="Clear"
        aria-label="Clear search"
        (click)="clearSearch()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- Page filter chips -->
    <div class="ss-panel-chips" role="tablist" aria-label="Filter by page">
      <button
        type="button"
        class="ss-panel-chip"
        [class.ss-panel-chip-active]="pageFilterControl.value === null"
        role="tab"
        [attr.aria-selected]="pageFilterControl.value === null"
        (click)="setPageFilter(null)"
      >
        All pages
      </button>
      <button
        type="button"
        class="ss-panel-chip"
        *ngFor="let p of pageOptions"
        [class.ss-panel-chip-active]="pageFilterControl.value === p.id"
        role="tab"
        [attr.aria-selected]="pageFilterControl.value === p.id"
        [matTooltip]="p.name"
        (click)="setPageFilter(p.id)"
      >
        <span>P{{ p.id }}</span>
        <span class="ss-panel-chip-count">{{ p.count }}</span>
      </button>
      <button
        *ngIf="selectMode"
        type="button"
        class="ss-panel-chip ss-panel-chip-action"
        (click)="selectAllVisible()"
      >
        Select all visible
      </button>
    </div>

    <!-- Grouped list -->
    <div class="ss-panel-list" role="list">
      <ng-container *ngIf="groups.length > 0; else emptyState">
        <section class="ss-panel-group" *ngFor="let g of groups; trackBy: trackByGroup">
          <header class="ss-panel-group-header">
            <span class="ss-panel-group-name">{{ g.pageName }}</span>
            <span class="ss-panel-group-count">{{ g.items.length }}</span>
          </header>
          <app-screenshot-card
            *ngFor="let s of g.items; trackBy: trackByItem"
            [screenshot]="s"
            [selectMode]="selectMode"
            [selected]="selectedIds.has(s.id)"
            [onCurrentPage]="s.pageId === currentPageId"
            [activeLocateScreenshotId]="activeLocateScreenshotId"
            (locate)="onCardLocate($event)"
            (annotate)="onCardAnnotate($event)"
            (share)="onCardShare($event)"
            (delete)="onCardDelete($event)"
            (copy)="onCardCopy($event)"
            (selectToggle)="onCardSelectToggle($event)"
          ></app-screenshot-card>
        </section>
      </ng-container>

      <ng-template #emptyState>
        <div class="ss-panel-empty" *ngIf="totalCount === 0; else noResults">
          <mat-icon class="ss-panel-empty-icon">photo_camera</mat-icon>
          <p class="ss-panel-empty-title">No screenshots yet</p>
          <p class="ss-panel-empty-hint">Press <kbd>S</kbd> or click the camera icon to capture a region.</p>
        </div>
        <ng-template #noResults>
          <div class="ss-panel-empty">
            <mat-icon class="ss-panel-empty-icon">search_off</mat-icon>
            <p class="ss-panel-empty-title">No matching clips</p>
            <p class="ss-panel-empty-hint">Try clearing the search or page filter.</p>
          </div>
        </ng-template>
      </ng-template>
    </div>

    <!-- Bulk action bar in select mode -->
    <footer class="ss-panel-bulk-bar" *ngIf="selectMode && selectedCount > 0">
      <span class="ss-panel-bulk-count">{{ selectedCount }} selected</span>
      <div class="ss-panel-bulk-actions">
        <button
          type="button"
          class="ss-panel-btn ss-panel-btn-primary"
          (click)="bulkShare()"
        >
          <mat-icon>share</mat-icon>
          <span>Share</span>
        </button>
        <button
          type="button"
          class="ss-panel-btn ss-panel-btn-danger"
          (click)="bulkDelete()"
        >
          <mat-icon>delete</mat-icon>
          <span>Delete</span>
        </button>
      </div>
    </footer>

    <!-- Visible-count footer when browsing -->
    <footer class="ss-panel-footer" *ngIf="!selectMode && groups.length > 0">
      <span>{{ visibleCount }} of {{ totalCount }} shown</span>
    </footer>
  </aside>
</ng-container>
